import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { saveTaskLists, loadTaskLists, subscribeToTaskLists } from '../firebase/taskService';
import './TaskList.css';

function TaskList() {
  const [lists, setLists] = useState([]);
  const [newListName, setNewListName] = useState('');
  const [showNewListForm, setShowNewListForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState('');
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  // Load lists from Firestore on mount
  useEffect(() => {
    if (currentUser) {
      const loadData = async () => {
        setLoading(true);
        const result = await loadTaskLists(currentUser.id);
        if (result.success) {
          setLists(result.lists);
        } else {
          // Fallback to localStorage if Firestore fails
          const savedLists = localStorage.getItem(`lists_${currentUser.id}`);
          if (savedLists) {
            setLists(JSON.parse(savedLists));
          } else {
            const defaultList = {
              id: Date.now(),
              name: 'My Tasks',
              tasks: []
            };
            setLists([defaultList]);
          }
        }
        setLoading(false);
      };
      loadData();
    }
  }, [currentUser]);

  // Save lists to Firestore whenever they change
  useEffect(() => {
    if (currentUser && lists.length > 0 && !loading) {
      const saveData = async () => {
        setSyncStatus('Saving...');
        const result = await saveTaskLists(currentUser.id, lists);
        if (result.success) {
          setSyncStatus('Saved');
          setTimeout(() => setSyncStatus(''), 2000);
        } else {
          setSyncStatus('Error saving');
          // Fallback to localStorage
          localStorage.setItem(`lists_${currentUser.id}`, JSON.stringify(lists));
        }
      };
      saveData();
    }
  }, [lists, currentUser, loading]);

  const handleCreateList = (e) => {
    e.preventDefault();
    if (!newListName.trim()) return;

    const newList = {
      id: Date.now(),
      name: newListName,
      tasks: []
    };

    setLists([...lists, newList]);
    setNewListName('');
    setShowNewListForm(false);
  };

  const handleAddTask = (listId, taskText) => {
    if (!taskText.trim()) return;

    const task = {
      id: Date.now(),
      text: taskText,
      completed: false,
      notes: '',
      createdAt: new Date().toISOString()
    };

    setLists(lists.map(list =>
      list.id === listId
        ? { ...list, tasks: [task, ...list.tasks] }
        : list
    ));
  };

  const updateTaskNotes = (listId, taskId, notes) => {
    setLists(lists.map(list =>
      list.id === listId
        ? {
            ...list,
            tasks: list.tasks.map(task =>
              task.id === taskId ? { ...task, notes } : task
            )
          }
        : list
    ));
  };

  const toggleTask = (listId, taskId) => {
    setLists(lists.map(list =>
      list.id === listId
        ? {
            ...list,
            tasks: list.tasks.map(task =>
              task.id === taskId ? { ...task, completed: !task.completed } : task
            )
          }
        : list
    ));
  };

  const deleteTask = (listId, taskId) => {
    setLists(lists.map(list =>
      list.id === listId
        ? { ...list, tasks: list.tasks.filter(task => task.id !== taskId) }
        : list
    ));
  };

  const deleteList = (listId) => {
    if (lists.length === 1) {
      alert('You must have at least one list!');
      return;
    }
    if (window.confirm('Are you sure you want to delete this list and all its tasks?')) {
      setLists(lists.filter(list => list.id !== listId));
    }
  };

  const renameList = (listId, newName) => {
    setLists(lists.map(list =>
      list.id === listId ? { ...list, name: newName } : list
    ));
  };

  const reorderTasks = (listId, startIndex, endIndex) => {
    setLists(lists.map(list => {
      if (list.id === listId) {
        const newTasks = Array.from(list.tasks);
        const [removed] = newTasks.splice(startIndex, 1);
        newTasks.splice(endIndex, 0, removed);
        return { ...list, tasks: newTasks };
      }
      return list;
    }));
  };

  const clearAllTasks = (listId) => {
    if (window.confirm('Are you sure you want to clear all tasks from this list?')) {
      setLists(lists.map(list =>
        list.id === listId ? { ...list, tasks: [] } : list
      ));
    }
  };

  const reorderLists = (startIndex, endIndex) => {
    const newLists = Array.from(lists);
    const [removed] = newLists.splice(startIndex, 1);
    newLists.splice(endIndex, 0, removed);
    setLists(newLists);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const totalTasks = lists.reduce((sum, list) => sum + list.tasks.length, 0);
  const completedCount = lists.reduce(
    (sum, list) => sum + list.tasks.filter(task => task.completed).length,
    0
  );

  if (loading) {
    return (
      <div className="task-list-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="task-list-container">
      <header className="task-header">
        <div className="task-header-content">
          <div className="header-left">
            <button onClick={() => navigate('/')} className="back-button">
              ← Home
            </button>
            <h1>My Task Lists</h1>
            {syncStatus && (
              <span className={`sync-status ${syncStatus === 'Error saving' ? 'error' : ''}`}>
                {syncStatus}
              </span>
            )}
          </div>
          <div className="user-info">
            <span>{currentUser?.email}</span>
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="task-main">
        <div className="task-content">
          <div className="task-stats">
            <div className="stat">
              <span className="stat-number">{lists.length}</span>
              <span className="stat-label">Lists</span>
            </div>
            <div className="stat">
              <span className="stat-number">{totalTasks}</span>
              <span className="stat-label">Total Tasks</span>
            </div>
            <div className="stat">
              <span className="stat-number">{completedCount}</span>
              <span className="stat-label">Completed</span>
            </div>
            <div className="stat">
              <span className="stat-number">{totalTasks - completedCount}</span>
              <span className="stat-label">Remaining</span>
            </div>
          </div>

          <div className="create-list-section">
            {!showNewListForm ? (
              <button
                onClick={() => setShowNewListForm(true)}
                className="create-list-button"
              >
                + Create New List
              </button>
            ) : (
              <form onSubmit={handleCreateList} className="new-list-form">
                <input
                  type="text"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="Enter list name..."
                  className="list-name-input"
                  autoFocus
                />
                <button type="submit" className="save-list-button">
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowNewListForm(false);
                    setNewListName('');
                  }}
                  className="cancel-button"
                >
                  Cancel
                </button>
              </form>
            )}
          </div>

          <div className="lists-grid">
            {lists.map((list, index) => (
              <TaskListCard
                key={list.id}
                list={list}
                listIndex={index}
                onAddTask={handleAddTask}
                onToggleTask={toggleTask}
                onDeleteTask={deleteTask}
                onDeleteList={deleteList}
                onUpdateNotes={updateTaskNotes}
                onRenameList={renameList}
                onReorderTasks={reorderTasks}
                onClearAllTasks={clearAllTasks}
                onReorderLists={reorderLists}
              />
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

function TaskListCard({ list, listIndex, onAddTask, onToggleTask, onDeleteTask, onDeleteList, onUpdateNotes, onRenameList, onReorderTasks, onClearAllTasks, onReorderLists }) {
  const [newTask, setNewTask] = useState('');
  const [editingNoteTaskId, setEditingNoteTaskId] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [isRenamingList, setIsRenamingList] = useState(false);
  const [listNameInput, setListNameInput] = useState(list.name);
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newTask.trim()) {
      onAddTask(list.id, newTask);
      setNewTask('');
    }
  };

  const handleNoteClick = (task) => {
    setEditingNoteTaskId(task.id);
    setNoteText(task.notes || '');
  };

  const handleSaveNote = () => {
    if (editingNoteTaskId) {
      onUpdateNotes(list.id, editingNoteTaskId, noteText);
      setEditingNoteTaskId(null);
      setNoteText('');
    }
  };

  const handleCancelNote = () => {
    setEditingNoteTaskId(null);
    setNoteText('');
  };

  const handleRenameSubmit = () => {
    if (listNameInput.trim() && listNameInput !== list.name) {
      onRenameList(list.id, listNameInput.trim());
    }
    setIsRenamingList(false);
  };

  const handleRenameFocus = () => {
    setIsRenamingList(true);
    setListNameInput(list.name);
  };

  const completedCount = list.tasks.filter(task => task.completed).length;

  // Sort tasks: incomplete first, completed last
  const sortedTasks = [...list.tasks].sort((a, b) => {
    if (a.completed === b.completed) return 0;
    return a.completed ? 1 : -1;
  });

  const handleDragStart = (e, taskId) => {
    setDraggedTaskId(taskId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropTaskId) => {
    e.preventDefault();
    if (draggedTaskId === dropTaskId) return;

    const draggedIndex = list.tasks.findIndex(t => t.id === draggedTaskId);
    const dropIndex = list.tasks.findIndex(t => t.id === dropTaskId);

    if (draggedIndex !== -1 && dropIndex !== -1) {
      onReorderTasks(list.id, draggedIndex, dropIndex);
    }
    setDraggedTaskId(null);
  };

  const handleDragEnd = () => {
    setDraggedTaskId(null);
  };

  const handleListDragStart = (e) => {
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('listIndex', listIndex.toString());
  };

  const handleListDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleListDrop = (e) => {
    e.preventDefault();
    const draggedIndex = parseInt(e.dataTransfer.getData('listIndex'));
    if (draggedIndex !== listIndex) {
      onReorderLists(draggedIndex, listIndex);
    }
    setIsDragging(false);
  };

  const handleListDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <div
      className={`list-card ${isDragging ? 'dragging-list' : ''}`}
      draggable="true"
      onDragStart={handleListDragStart}
      onDragOver={handleListDragOver}
      onDrop={handleListDrop}
      onDragEnd={handleListDragEnd}
    >
      <div className="list-card-header">
        <span className="list-drag-handle" title="Drag to reorder list">⋮⋮</span>
        {isRenamingList ? (
          <input
            type="text"
            value={listNameInput}
            onChange={(e) => setListNameInput(e.target.value)}
            onBlur={handleRenameSubmit}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleRenameSubmit();
              if (e.key === 'Escape') setIsRenamingList(false);
            }}
            className="list-name-edit"
            autoFocus
          />
        ) : (
          <h3 onClick={handleRenameFocus} style={{ cursor: 'pointer' }} title="Click to rename">
            {list.name}
          </h3>
        )}
        <div className="list-header-actions">
          <button
            onClick={() => onClearAllTasks(list.id)}
            className="clear-all-button"
            title="Clear all tasks"
          >
            🗑️
          </button>
          <button
            onClick={() => onDeleteList(list.id)}
            className="delete-list-button"
            title="Delete list"
          >
            ×
          </button>
        </div>
      </div>

      <div className="list-card-stats">
        <span>{list.tasks.length} tasks</span>
        <span>{completedCount} completed</span>
      </div>

      <form onSubmit={handleSubmit} className="add-task-form-inline">
        <input
          type="text"
          value={newTask}
          onChange={(e) => setNewTask(e.target.value)}
          placeholder="Add a task..."
          className="task-input-inline"
        />
        <button type="submit" className="add-button-inline">
          +
        </button>
      </form>

      <div className="tasks-list-inline">
        {list.tasks.length === 0 ? (
          <div className="empty-state-inline">
            <p>No tasks yet</p>
          </div>
        ) : (
          sortedTasks.map(task => (
            <div
              key={task.id}
              className={`task-item-inline ${task.completed ? 'completed' : ''} ${draggedTaskId === task.id ? 'dragging' : ''}`}
              draggable="true"
              onDragStart={(e) => handleDragStart(e, task.id)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, task.id)}
              onDragEnd={handleDragEnd}
            >
              <span className="drag-handle" title="Drag to reorder">⋮⋮</span>
              <input
                type="checkbox"
                checked={task.completed}
                onChange={() => onToggleTask(list.id, task.id)}
                className="task-checkbox-inline"
              />
              <span className="task-text-inline">{task.text}</span>

              {task.notes ? (
                <div className="note-bubble-container">
                  <button
                    onClick={() => handleNoteClick(task)}
                    className="note-bubble"
                    title="View/Edit note"
                  >
                    📝
                  </button>
                  <div className="note-tooltip">{task.notes}</div>
                </div>
              ) : (
                <button
                  onClick={() => handleNoteClick(task)}
                  className="add-note-button"
                  title="Add note"
                >
                  +📝
                </button>
              )}

              <button
                onClick={() => onDeleteTask(list.id, task.id)}
                className="delete-button-inline"
              >
                ×
              </button>
            </div>
          ))
        )}
      </div>

      {editingNoteTaskId && (
        <div className="note-modal-overlay" onClick={handleCancelNote}>
          <div className="note-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Edit Note</h4>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a note for this task..."
              className="note-textarea"
              autoFocus
            />
            <div className="note-modal-buttons">
              <button onClick={handleSaveNote} className="save-note-btn">
                Save
              </button>
              <button onClick={handleCancelNote} className="cancel-note-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskList;
